// If you are adding a term to the ActionSubject glossary,
// please add a member of the Data team to your PR

// UI EVENTS

// For UI actionSubjects
const uiElements = {
  button: 'button',
  checkbox: 'checkbox',
  drawerNavigationItem: 'drawerNavigationItem',
  dropdown: 'dropdown',
  dropdownItem: 'dropdownItem',
  icon: 'icon',
  inlineDialog: 'inlineDialog',
  input: 'input',
  link: 'link',
  menu: 'menu',
  menuItem: 'menuItem',
  option: 'option',
  pill: 'pill',
  radioButton: 'radioButton',
  tile: 'tile',
  toggle: 'toggle',
  tooltip: 'tooltip',
} as const;

// To use with sendViewedComponentEvent,
// sendClosedComponentEvent, sendDismissedComponentEvent
const components = {
  badge: 'badge',
  banner: 'banner',
  cardComposer: 'cardComposer',
  inlineDialog: 'inlineDialog',
  input: 'input',
  modal: 'modal',
  overlay: 'overlay',
  section: 'section',
  sectionItem: 'sectionItem',
  tab: 'tab', // for Home tabs
} as const;

// TRACK EVENTS

const entities = {
  board: 'board',
  card: 'card',
  comment: 'comment',
  label: 'label',
  list: 'list',
  member: 'member',
  organization: 'organization', // Instead of "team"
  powerUp: 'powerUp',
} as const;

// To use with sendUpdatedBoardFieldEvent
// See server/app/schema/board.js for parity
const boardFields = {
  background: 'background',
  calendarFeedEnabledPref: 'calendarFeedEnabledPref',
  cardCoversPref: 'cardCoversPref',
  cardAgingPref: 'cardAgingPref',
  commentsPref: 'commentsPref',
  description: 'description',
  hideVotesPref: 'hideVotesPref',
  invitationsPref: 'invitationsPref',
  isTemplate: 'isTemplate', // No "Pref"
  name: 'name',
  selfJoinPref: 'selfJoinPref',
  star: 'star',
  visibility: 'visibility', // Permission level
  votingPref: 'votingPref',
} as const;

const cardFields = {
  cover: 'cover',
  coverSize: 'coverSize',
  coverBrightness: 'coverBrightness',
} as const;

const emails = {
  accountEmail: 'accountEmail',
  email: 'email',
  marketingEmail: 'marketingEmail',
  primaryEmail: 'primaryEmail',
  secondaryEmail: 'secondaryEmail',
} as const;

const invitations = {
  boardInvitation: 'boardInvitation', // for confirmed members
  emailBoardInvitation: 'emailBoardInvitation', // for unconfirmed members
  invitation: 'invitation',
  inviteAcceptanceNotification: 'inviteAcceptanceNotification',
  inviteToBoardLink: 'inviteToBoardLink',
  organizationInvitation: 'organizationInvitation',
} as const;

// OTHER

const otherActionSubjects = {
  accountSwitcher: 'accountSwitcher',
  app: 'app',
  atlassianSwitcherHeaderButton: 'atlassianSwitcherHeaderButton',
  attachment: 'attachment',
  avatar: 'avatar',
  billingContact: 'billingContact',
  billingFrequency: 'billingFrequency',
  bcPayment: 'bcPayment',
  bcFreeTrial: 'bcFreeTrial',
  bcFreeTrialUpsell: 'bcFreeTrialUpsell',
  boardOrganization: 'boardOrganization',
  businessClass: 'businessClass',
  cacheSyncing: 'cacheSyncing',
  cardCopy: 'cardCopy',
  cardMapLocation: 'cardMapLocation',
  cardMapLocationName: 'cardMapLocationName',
  collection: 'collection',
  colorBlindFriendlyMode: 'colorBlindFriendlyMode',
  commentsPermissions: 'commentsPermissions',
  confluenceLearningExperimentMetaData: 'confluenceLearningExperimentMetaData',
  creditCard: 'creditCard',
  descriptionEdit: 'descriptionEdit',
  download: 'download',
  dueDate: 'dueDate',
  edit: 'edit',
  editor: 'editor',
  enterKey: 'enterKey',
  embed: 'embed',
  eventValidation: 'eventValidation',
  error: 'error',
  feature: 'feature',
  featureFlag: 'featureFlag',
  fetchAvailableSites: 'fetchAvailableSites',
  fileInputUpload: 'fileInputUpload',
  filter: 'filter',
  freeTrial: 'freeTrial',
  homeSectionItem: 'homeSectionItem',
  listLimit: 'listLimit',
  locale: 'locale',
  location: 'location',
  mention: 'mention',
  mode: 'mode',
  moonshotStep: 'moonshotStep',
  notification: 'notification',
  observer: 'observer',
  onboarding: 'onboarding',
  payment: 'payment',
  pluginOperationalEvent: 'pluginOperationalEvent',
  pluginScreenEvent: 'pluginScreenEvent',
  pluginTrackEvent: 'pluginTrackEvent',
  pluginUIEvent: 'pluginUIEvent',
  pluginViewedComponentEvent: 'pluginViewedComponentEvent',
  performance: 'performance',
  position: 'position',
  prompt: 'prompt',
  purchase: 'purchase',
  reaction: 'reaction',
  reactionPicker: 'reactionPicker',
  request: 'request',
  search: 'search',
  searchSave: 'searchSave',
  serviceWorker: 'serviceWorker',
  session: 'session',
  shortcut: 'shortcut', // For keyboard shortcuts
  sort: 'sort',
  step: 'step',
  sticker: 'sticker',
  storage: 'storage',
  suggestion: 'suggestion',
  survey: 'survey',
  taxExemptionCertificate: 'taxExemptionCertificate',
  team: 'team',
  teamBoardDeletionRestriction: 'teamBoardDeletionRestriction',
  teamBoardInvitationRestriction: 'teamBoardInvitationRestriction',
  teamBoardVisibilityRestriction: 'teamBoardVisibilityRestriction',
  teamMembershipRestriction: 'teamMembershipRestriction',
  teamTableView: 'teamTableView',
  textField: 'textField',
  trial: 'trial',
  trelloGold: 'trelloGold',
  ui: 'ui', // This should only be used for stock "UI viewed"-type events
  upload: 'upload',
  url: 'url',
  upsell: 'upsell',
} as const;

export const ActionSubject = {
  ...entities,
  ...boardFields,
  ...cardFields,
  ...components,
  ...uiElements,
  ...emails,
  ...invitations,
  ...otherActionSubjects,
} as const;

export type ActionSubjectType = typeof ActionSubject[keyof typeof ActionSubject];
